import React, { createContext, useContext, useState, useEffect } from 'react';
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Toaster } from "@/components/ui/sonner";

// Pages
import LandingPage from "@/pages/LandingPage";
import LoginPage from "@/pages/LoginPage";
import RegisterPage from "@/pages/RegisterPage";
import StandInDashboard from "@/pages/StandInDashboard";
import ProductionDashboard from "@/pages/ProductionDashboard";
import AdminDashboard from "@/pages/AdminDashboard";
import SharedProfilePage from "@/pages/SharedProfilePage";
import ChitCalculatorPage from "@/pages/ChitCalculatorPage";
import TermsAndConditionsPage from "@/pages/TermsAndConditionsPage";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import VerifyEmailPage from "@/pages/VerifyEmailPage";
import PrivacyPolicyPage from "@/pages/PrivacyPolicyPage";
import TrainingPage from "@/pages/TrainingPage";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);
  const [isLoggingIn, setIsLoggingIn] = useState(false);

  useEffect(() => {
    // Skip fetch if we just logged in (user already set from login response)
    if (isLoggingIn) {
      setIsLoggingIn(false);
      setLoading(false);
      return;
    }
    if (token) {
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(response.data);
    } catch (error) {
      console.error('Error fetching user:', error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { access_token, user: userData } = response.data;
    setIsLoggingIn(true);
    localStorage.setItem('token', access_token);
    setUser(userData);
    setToken(access_token);
    setLoading(false);
    return userData;
  };

  const register = async (data) => {
    const response = await axios.post(`${API}/auth/register`, data);
    const { access_token, user: userData } = response.data;
    setIsLoggingIn(true);
    localStorage.setItem('token', access_token);
    setUser(userData);
    setToken(access_token);
    setLoading(false);
    return userData;
  };

  const logout = () => {
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, token, loading, login, register, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

// Protected Route
const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#050505]">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#D4AF37]"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (allowedRoles && !allowedRoles.includes(user.role)) {
    // Redirect to appropriate dashboard based on role
    if (user.role === 'admin') return <Navigate to="/admin" replace />;
    if (user.role === 'production') return <Navigate to="/production" replace />;
    if (user.role === 'standin') return <Navigate to="/standin" replace />;
    return <Navigate to="/" replace />;
  }

  return children;
};

// Email Verification Banner
const EmailVerifyBanner = () => {
  const { user, token } = useAuth();
  const [dismissed, setDismissed] = React.useState(false);
  const [resending, setResending] = React.useState(false);

  if (!user || user.email_verified || user.role === 'admin' || dismissed) return null;

  const resend = async () => {
    setResending(true);
    try {
      await axios.post(`${API}/auth/resend-verification`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch {}
    setResending(false);
  };

  return (
    <div className="bg-[#F59E0B]/10 border-b border-[#F59E0B]/30 px-6 py-3 flex items-center justify-between" data-testid="email-verify-banner">
      <p className="text-[#F59E0B] text-sm">
        Please verify your email address. Check your inbox for a verification link.
      </p>
      <div className="flex items-center gap-3">
        <button onClick={resend} disabled={resending} className="text-[#D4AF37] text-sm font-semibold hover:underline disabled:opacity-50" data-testid="resend-verification-btn">
          {resending ? 'Sending...' : 'Resend'}
        </button>
        <button onClick={() => setDismissed(true)} className="text-white/40 text-sm hover:text-white">Dismiss</button>
      </div>
    </div>
  );
};

// Dashboard Router
const DashboardRouter = () => {
  const { user } = useAuth();
  
  if (!user) return <Navigate to="/login" replace />;
  
  switch (user.role) {
    case 'admin':
      return <Navigate to="/admin" replace />;
    case 'production':
      return <Navigate to="/production" replace />;
    case 'standin':
      return <Navigate to="/standin" replace />;
    default:
      return <Navigate to="/" replace />;
  }
};

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          {/* Public Routes */}
          <Route path="/" element={<LandingPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/share/:shareToken" element={<SharedProfilePage />} />
          <Route path="/chit-calculator" element={<ChitCalculatorPage />} />
          <Route path="/terms" element={<TermsAndConditionsPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/verify-email" element={<VerifyEmailPage />} />
          <Route path="/privacy" element={<PrivacyPolicyPage />} />
          <Route path="/training" element={<TrainingPage />} />
          
          {/* Dashboard Router */}
          <Route path="/dashboard" element={<DashboardRouter />} />
          
          {/* Stand-in Routes */}
          <Route
            path="/standin/*"
            element={
              <ProtectedRoute allowedRoles={['standin']}>
                <EmailVerifyBanner />
                <StandInDashboard />
              </ProtectedRoute>
            }
          />
          
          {/* Production Routes */}
          <Route
            path="/production/*"
            element={
              <ProtectedRoute allowedRoles={['production']}>
                <EmailVerifyBanner />
                <ProductionDashboard />
              </ProtectedRoute>
            }
          />
          
          {/* Admin Routes */}
          <Route
            path="/admin/*"
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <AdminDashboard />
              </ProtectedRoute>
            }
          />
        </Routes>
        <Toaster position="top-right" richColors />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
